import { useEffect, useState } from "react";
import { recordManufacturingPurchaseOrderInvoice } from "@/lib/manufacturing/purchaseOrderClient";
import { formatCcEmailList } from "@/lib/manufacturing/ccEmails";
import { poHasInvoiceRecord } from "@/lib/manufacturing/purchaseOrderInvoice";
import type { MfgPurchaseOrder } from "@/lib/manufacturing/purchaseOrderTypes";
import { CcEmailChipInput } from "@/components/manufacturing/CcEmailChipInput";
import { inputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

export function PurchaseOrderInvoiceSection(props: {
  order: MfgPurchaseOrder;
  enabled: boolean;
  onUpdated: (order: MfgPurchaseOrder) => void;
  onToast: (msg: string, ok: boolean) => void;
  defaultInvoiceCcEmails?: string[];
}) {
  const { order } = props;
  const recorded = poHasInvoiceRecord(order);
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState(() =>
    order.invoice_total_usd != null ? String(order.invoice_total_usd) : order.total != null ? String(order.total) : ""
  );
  const [terms, setTerms] = useState(order.invoice_pay_terms ?? "");
  const [delivery, setDelivery] = useState(order.invoice_delivery_on ?? "");
  const [ccEmails, setCcEmails] = useState<string[]>(() =>
    order.invoice_cc_emails?.length
      ? order.invoice_cc_emails
      : props.defaultInvoiceCcEmails ?? []
  );

  useEffect(() => {
    const fromOrder = order.invoice_cc_emails ?? [];
    setCcEmails(fromOrder.length ? fromOrder : props.defaultInvoiceCcEmails ?? []);
    setAmount(
      order.invoice_total_usd != null ? String(order.invoice_total_usd) : order.total != null ? String(order.total) : "",
    );
    setTerms(order.invoice_pay_terms ?? "");
    setDelivery(order.invoice_delivery_on ?? "");
    // Seed from the open PO. Don't loop on array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id, order.invoice_total_usd, order.invoice_pay_terms, order.invoice_delivery_on, formatCcEmailList(order.invoice_cc_emails ?? [])]);

  const canRecord = order.status !== "draft" && order.status !== "cancelled";

  async function submit() {
    const invoice_total_usd = Number(amount.replace(/[$,]/g, ""));
    if (!Number.isFinite(invoice_total_usd) || invoice_total_usd < 0) {
      props.onToast("Enter the invoice amount.", false);
      return;
    }
    if (!terms.trim()) {
      props.onToast("Pay terms are required (for example Net 30).", false);
      return;
    }
    setBusy(true);
    const res = await recordManufacturingPurchaseOrderInvoice({
      po_id: order.id,
      invoice_total_usd,
      invoice_pay_terms: terms.trim(),
      invoice_delivery_on: delivery.trim() || null,
      invoice_cc_emails: ccEmails,
    });
    setBusy(false);
    if (!res.ok) {
      props.onToast(res.error, false);
      return;
    }
    props.onToast(`Invoice recorded on PO ${res.data.order.po_number}`, true);
    props.onUpdated(res.data.order);
  }

  const recordedCc = formatCcEmailList(order.invoice_cc_emails ?? []);

  return (
    <div>
      <div className="text-xs font-semibold text-gray-800 dark:text-zinc-100">Vendor invoice</div>
      <p className="text-[11px] text-gray-500">
        Invoice amount replaces the predicted PO total. Pay terms are required; type how this vendor
        works (Net 30, Due on receipt, …). Extra CC emails are recognized here and stored on this PO.
      </p>
      {recorded ? (
        <p className="mt-1 text-[11px] text-emerald-800 dark:text-emerald-300">
          Recorded {order.invoice_total_usd != null ? `$${order.invoice_total_usd.toFixed(2)}` : ""}
          {order.invoice_pay_terms ? ` · ${order.invoice_pay_terms}` : ""}
          {order.invoice_delivery_on ? ` · delivery ${order.invoice_delivery_on}` : ""}
          {recordedCc ? ` · CC ${recordedCc}` : ""}
        </p>
      ) : (
        <p className="mt-1 text-[11px] text-amber-800 dark:text-amber-200">Needs invoice</p>
      )}
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="text-[11px]">
          Invoice amount
          <input
            className={`${inputClass} mt-0.5 w-28`}
            value={amount}
            disabled={!props.enabled || busy || !canRecord}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
          />
        </label>
        <label className="text-[11px]">
          Pay terms
          <input
            className={`${inputClass} mt-0.5 w-40`}
            value={terms}
            disabled={!props.enabled || busy || !canRecord}
            onChange={(e) => setTerms(e.target.value)}
            placeholder="Net 30"
          />
        </label>
        <label className="text-[11px]">
          Delivery date
          <input
            type="date"
            className={`${inputClass} mt-0.5`}
            value={delivery}
            disabled={!props.enabled || busy || !canRecord}
            onChange={(e) => setDelivery(e.target.value)}
          />
        </label>
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={!props.enabled || busy || !canRecord}
          onClick={() => void submit()}
        >
          {recorded ? "Update invoice" : "Record invoice"}
        </button>
      </div>
      <label className="mt-2 block text-[11px]">
        <span className="font-semibold">Invoice CC emails</span>
        <CcEmailChipInput
          emails={ccEmails}
          onChange={setCcEmails}
          disabled={!props.enabled || busy || !canRecord}
          placeholder="Type an email, then Enter or comma"
        />
      </label>
    </div>
  );
}
